"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { Button } from "@/components/ui/button"
import { useMobile } from "@/hooks/use-mobile"
import { createClient } from "@/utils/supabase/client"
import {
  LOGIN_PROMPT_SNOOZE_KEY,
  deriveLoginPromptOpenDelayMs,
  parseSnoozedAt,
  readLoginPromptSnoozedAt,
  writeLoginPromptSnoozedAt,
} from "@/lib/auth/login-prompt-snooze"

const AUTH_PATH_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-phone",
  "/recover",
]

function isExcludedPath(pathname: string): boolean {
  if (pathname === "/") return true
  return AUTH_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

type GlobalLoginPromptProps = {
  initialUserId: string | null
}

export function GlobalLoginPrompt({ initialUserId }: GlobalLoginPromptProps) {
  const t = useTranslations("authModal")
  const tPrompt = useTranslations("globalLoginPrompt")
  const tCommon = useTranslations("common")
  const isMobile = useMobile()
  const pathname = usePathname()
  const router = useRouter()
  const [userId, setUserId] = useState<string | null>(initialUserId)
  const [open, setOpen] = useState(false)
  // Dismissal memory is a timestamp, not a pathname: it has to survive the next
  // navigation and the next reload, which is exactly what the old
  // `dismissedPath` state could not do. Read once on mount (null during SSR, so
  // no hydration divergence — `open` starts false either way).
  const [snoozedAt, setSnoozedAt] = useState<number | null>(() => readLoginPromptSnoozedAt())
  const userIdRef = useRef(initialUserId)
  userIdRef.current = userId

  useEffect(() => {
    setUserId(initialUserId)
  }, [initialUserId])

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      const nextUserId = user?.id ?? null
      setUserId(nextUserId)
      if (nextUserId) setOpen(false)
    })
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUserId = session?.user?.id ?? null
      setUserId(nextUserId)
      if (nextUserId) setOpen(false)
    })
    return () => subscription.unsubscribe()
  }, [])

  // Cross-tab sync (same contract as the order/cart caches): a dismissal in one
  // tab silences the others, so a guest with three feed tabs open is not asked
  // three times.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== LOGIN_PROMPT_SNOOZE_KEY) return
      setSnoozedAt(parseSnoozedAt(event.newValue))
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  const eligible = useMemo(() => {
    if (userId) return false
    if (!pathname || isExcludedPath(pathname)) return false
    return true
  }, [userId, pathname])

  useEffect(() => {
    if (!eligible) {
      setOpen(false)
      return
    }
    // One timer covers both cases: settle delay when nothing is snoozed, and
    // "rest of the snooze window + settle delay" when it is — so the prompt also
    // comes back on a page the guest is just sitting on, not only on the next
    // navigation.
    const delayMs = deriveLoginPromptOpenDelayMs({ snoozedAt, now: Date.now() })
    const timer = window.setTimeout(async () => {
      if (userIdRef.current) return
      const supabase = createClient()
      const { data: { user } } = await supabase.auth.getUser()
      if (user?.id) {
        setUserId(user.id)
        setOpen(false)
        return
      }
      setOpen(true)
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [eligible, pathname, snoozedAt])

  const handleClose = useCallback(() => {
    const at = Date.now()
    writeLoginPromptSnoozedAt(at)
    setSnoozedAt(at)
    setOpen(false)
  }, [])

  const callbackUrl =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : pathname || "/feed"

  // Chrome-free by construction: the close control belongs to whichever
  // primitive renders this body (`DialogCard` on desktop, `DockSheet` on
  // mobile). Drawing it INSIDE this shared body is exactly what forced the
  // sheet into `chrome="none"` — HANDOFF_DIALOG_CHROME §3.
  const content = (
    <div className="flex flex-col items-center gap-6">
      <div className="relative h-20 w-20 overflow-hidden rounded-2xl bg-foreground/10">
        {/* `sizes` is not optional next to `fill`: without it Next assumes the
            viewport width and every device picks a different `deviceSizes`
            bucket — 14 of them, x2 formats, each a separately billed
            transformation of the same 80 px logo. */}
        <Image src="/kraahl_logo.png" alt="Kraahl" fill sizes="80px" className="object-cover" priority />
      </div>

      <div className="space-y-2 text-center">
        <h2 className="text-xl font-semibold text-foreground">{t("joinKraahl")}</h2>
        <p className="text-sm text-muted-foreground text-pretty">{tPrompt("message")}</p>
      </div>

      <div className="w-full space-y-3">
        <Button
          variant="foil"
          className="h-12 w-full rounded-full text-base font-semibold"
          size="lg"
          onClick={() => {
            router.push(`/register?callbackUrl=${encodeURIComponent(callbackUrl)}`)
            handleClose()
          }}
        >
          {t("register")}
        </Button>
        <Button
          variant="outline"
          className="h-12 w-full rounded-full border-foreground/20 bg-transparent text-base font-semibold text-foreground hover:bg-foreground/10 hover:text-foreground"
          size="lg"
          onClick={() => {
            router.push(`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`)
            handleClose()
          }}
        >
          {t("logIn")}
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground/70">
        {tCommon.rich("agreeTerms", {
          privacy: (chunks) => (
            <Link href="/privacy" className="underline hover:text-foreground" onClick={handleClose}>
              {chunks}
            </Link>
          ),
          terms: (chunks) => (
            <Link href="/terms" className="underline hover:text-foreground" onClick={handleClose}>
              {chunks}
            </Link>
          ),
        })}
      </p>
    </div>
  )

  if (!eligible) return null

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleClose()
        }}
        // The body opens with its own hero (logo + `text-xl` heading), so a
        // chrome title would be a second one — handle + close only. Mirror of
        // the desktop `chrome="closeOnly"` below.
        chrome="handleOnly"
        contentSized
        zIndex={10060}
      >
        {content}
      </DockSheet>
    )
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose()
      }}
      chrome="closeOnly"
      a11yTitle={t("joinKraahl")}
      panelClassName="max-w-sm"
    >
      {content}
    </DialogCard>
  )
}
